import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Lock, Check, Loader2, AlertCircle, Shield, Crown, Zap, Building2, Gift } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { apiService } from '@/lib/api';
import { toast } from 'sonner';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const TIERS = [
  {
    id: 'basic',
    name: 'Basic',
    price: 39,
    icon: Zap,
    color: 'blue',
    description: 'Perfect for getting started',
    features: [
      '1 active project',
      'Single user (1 seat)',
      'Unlimited draw requests',
      'Standard draw checklists',
      'Add-ons à la carte ($19/mo)',
      'Email support',
    ],
    popular: false,
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 69,
    icon: Crown,
    color: 'indigo',
    description: 'For growing businesses',
    features: [
      'Up to 2 active projects',
      'Add-ons à la carte ($19/mo each)',
      'AI draw readiness & missing-doc detection',
      'Contractor Payment Report',
      'Lender-ready & branded exports',
      'Priority support',
    ],
    popular: true,
  },
  {
    id: 'advanced',
    name: 'Advanced',
    price: 129,
    icon: Building2,
    color: 'indigo',
    description: 'For growing builders with several projects',
    features: [
      'Up to 4 active projects',
      'Up to 2 seats',
      'Everything in Pro',
      'Add-ons à la carte ($19/mo each)',
      'Contractor Payment Report',
      'Priority support',
    ],
    popular: false,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 199,
    icon: Building2,
    color: 'slate',
    description: 'For large-scale operations',
    features: [
      'Unlimited projects',
      'Up to 4 seats',
      'Everything in Advanced',
      'All 4 add-ons included (no à la carte fees)',
      'Roles, permissions & audit logs',
      'White-labeled exports',
      'Dedicated onboarding',
    ],
    popular: false,
    contact_required: true,  // Must contact sales for Enterprise
  },
];

const CardForm = ({ user, selectedTier, discountCode, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setError(null);

    try {
      const { data: intentData } = await apiService.createSetupIntent(user.id);
      if (!intentData?.client_secret) {
        throw new Error('Failed to initialize payment setup');
      }

      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(
        intentData.client_secret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              email: user.email,
              name: user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim(),
            },
          },
        }
      );

      if (stripeError) {
        setError(stripeError.message);
        setIsProcessing(false);
        return;
      }

      // Pass discount code if present
      await apiService.updatePaymentMethod(user.id, setupIntent.payment_method, selectedTier.id, discountCode);
      onSuccess();
    } catch (err) {
      console.error('Payment setup error:', err);
      setError(err.response?.data?.detail || err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="payment-card-form">
      <div className="p-5 border border-slate-200 rounded-xl bg-white shadow-sm">
        <label className="block text-sm font-medium text-slate-700 mb-3">Card details</label>
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#1e293b',
                fontFamily: 'system-ui, -apple-system, sans-serif',
                '::placeholder': { color: '#94a3b8' },
              },
            },
          }}
        />
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-3" data-testid="payment-error">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <Button
        type="submit"
        disabled={!stripe || isProcessing}
        className="w-full bg-blue-600 hover:bg-blue-700 h-12 text-base font-medium"
        data-testid="add-card-submit-btn"
      >
        {isProcessing ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Setting up your account...
          </>
        ) : (
          <>
            <CreditCard className="w-5 h-5 mr-2" />
            Start 30-Day Free Trial
          </>
        )}
      </Button>

      <div className="flex items-center justify-center gap-2 text-sm text-slate-400">
        <Lock className="w-4 h-4" />
        <span>256-bit encryption. Cancel anytime. No charge for 30 days.</span>
      </div>
    </form>
  );
};

function MembershipPage({ user, onComplete }) {
  const navigate = useNavigate();
  const [selectedTier, setSelectedTier] = useState(null);
  const [discountCode, setDiscountCode] = useState('');
  const [discountValidation, setDiscountValidation] = useState(null);
  const [isValidatingDiscount, setIsValidatingDiscount] = useState(false);

  const handleSuccess = () => {
    onComplete();
    navigate('/dashboard', { replace: true });
  };

  const isFreeAccess = discountValidation?.valid && (discountValidation?.grants_free_access || discountValidation?.discount_type === 'free_access');
  const [isActivatingFree, setIsActivatingFree] = useState(false);

  const handleActivateFree = async () => {
    setIsActivatingFree(true);
    try {
      const res = await apiService.activateFreeMembership({ user_id: user.id, code: discountCode, tier: selectedTier?.id });
      localStorage.setItem('user', JSON.stringify(res.data));
      toast.success('Free access activated!');
      handleSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not activate free access');
    } finally {
      setIsActivatingFree(false);
    }
  };

  const handleValidateDiscount = async () => {
    if (!discountCode.trim() || !selectedTier) {
      setDiscountValidation(null);
      return;
    }
    
    setIsValidatingDiscount(true);
    try {
      const res = await apiService.validateDiscountCode(discountCode, selectedTier.id);
      setDiscountValidation({ valid: true, ...res.data });
      toast.success(`Discount applied: ${res.data.display}`);
    } catch (error) {
      setDiscountValidation({ valid: false, error: error.response?.data?.detail || 'Invalid code' });
      toast.error(error.response?.data?.detail || 'Invalid discount code');
    } finally {
      setIsValidatingDiscount(false);
    }
  };

  const getDisplayPrice = () => {
    if (!selectedTier) return 0;
    if (!discountValidation?.valid) return selectedTier.price;
    if (discountValidation.discount_type === 'percentage') {
      return Math.round(selectedTier.price * (1 - discountValidation.discount_value / 100));
    }
    if (discountValidation.discount_type === 'fixed') {
      return Math.max(0, selectedTier.price - discountValidation.discount_value);
    }
    if (discountValidation.discount_type === 'free_months') {
      return 0;
    }
    return selectedTier.price;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">D</span>
            </div>
            <span className="text-xl font-bold text-slate-900">DrawReady</span>
          </div>
          <span className="text-sm text-slate-500">Welcome, {user?.first_name || user?.name || user?.email}</span>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        {!selectedTier ? (
          <>
            {/* Tier Selection */}
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
                <Shield className="w-4 h-4" />
                30-day free trial on all plans
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3">
                Choose your plan
              </h1>
              <p className="text-lg text-slate-500 max-w-xl mx-auto">
                Start managing your construction projects like a pro. No charge until your trial ends.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto" data-testid="tier-selector">
              {TIERS.map((tier) => {
                const Icon = tier.icon;
                return (
                  <div
                    key={tier.id}
                    className={`relative rounded-2xl border-2 p-6 bg-white transition-all cursor-pointer hover:shadow-lg ${
                      tier.contact_required
                        ? 'border-purple-400 bg-purple-50'
                        : tier.popular
                          ? 'border-blue-500 shadow-md'
                          : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => {
                      if (tier.contact_required) {
                        window.open('mailto:support@drawready.com?subject=Enterprise%20Plan%20Inquiry', '_blank');
                        return;
                      }
                      setSelectedTier(tier);
                    }}
                    data-testid={`tier-option-${tier.id}`}
                  >
                    {tier.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="bg-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                          Most Popular
                        </span>
                      </div>
                    )}
                    {tier.contact_required && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="bg-purple-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                          Contact Us
                        </span>
                      </div>
                    )}

                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                      tier.contact_required ? 'bg-purple-100' : tier.popular ? 'bg-blue-100' : 'bg-slate-100'
                    }`}>
                      <Icon className={`w-6 h-6 ${tier.contact_required ? 'text-purple-600' : tier.popular ? 'text-blue-600' : 'text-slate-600'}`} />
                    </div>

                    <h3 className="text-xl font-bold text-slate-900">{tier.name}</h3>
                    <p className="text-sm text-slate-500 mt-1">{tier.description}</p>

                    <div className="mt-4 mb-6">
                      {tier.contact_required ? (
                        <span className="text-2xl font-bold text-purple-600">Custom Pricing</span>
                      ) : (
                        <>
                          <span className="text-4xl font-bold text-slate-900">${tier.price}</span>
                          <span className="text-slate-500">/mo</span>
                        </>
                      )}
                    </div>

                    <Button
                      className={`w-full mb-6 ${
                        tier.contact_required
                          ? 'bg-purple-600 hover:bg-purple-700'
                          : tier.popular
                            ? 'bg-blue-600 hover:bg-blue-700'
                            : 'bg-slate-800 hover:bg-slate-900'
                      }`}
                      data-testid={`select-tier-${tier.id}-btn`}
                    >
                      {tier.contact_required ? 'Contact Sales' : `Select ${tier.name}`}
                    </Button>

                    <ul className="space-y-3">
                      {tier.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                          <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <>
            {/* Card Entry */}
            <div className="max-w-lg mx-auto">
              <button
                onClick={() => {
                  setSelectedTier(null);
                  setDiscountValidation(null);
                }}
                className="text-sm text-slate-500 hover:text-slate-700 mb-6 flex items-center gap-1"
                data-testid="back-to-tiers-btn"
              >
                &larr; Back to plans
              </button>

              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
                <div className="text-center mb-6">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4 ${
                    selectedTier.popular ? 'bg-blue-100' : 'bg-slate-100'
                  }`}>
                    {React.createElement(selectedTier.icon, {
                      className: `w-7 h-7 ${selectedTier.popular ? 'text-blue-600' : 'text-slate-600'}`
                    })}
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900">
                    {selectedTier.name} Plan
                  </h2>
                  {discountValidation?.valid && getDisplayPrice() !== selectedTier.price ? (
                    <div className="mt-1">
                      <span className="text-slate-400 line-through mr-2">${selectedTier.price}/mo</span>
                      <span className="text-emerald-600 font-semibold">${getDisplayPrice()}/mo</span>
                      <p className="text-sm text-emerald-600 mt-1">{discountValidation.display}</p>
                    </div>
                  ) : (
                    <p className="text-slate-500 mt-1">
                      ${selectedTier.price}/mo after 30-day free trial
                    </p>
                  )}
                </div>

                {/* Discount Code Input */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-6">
                  <label className="text-sm text-slate-600 mb-2 block flex items-center gap-2">
                    <Gift className="w-4 h-4" />
                    Have a discount code?
                  </label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter code"
                      value={discountCode}
                      onChange={(e) => {
                        setDiscountCode(e.target.value.toUpperCase());
                        setDiscountValidation(null);
                      }}
                      className="flex-1"
                    />
                    <Button 
                      type="button"
                      onClick={handleValidateDiscount}
                      disabled={!discountCode || isValidatingDiscount}
                      variant="outline"
                    >
                      {isValidatingDiscount ? 'Checking...' : 'Apply'}
                    </Button>
                  </div>
                  {discountValidation && (
                    <div className={`mt-2 text-sm ${discountValidation.valid ? 'text-emerald-600' : 'text-red-500'}`}>
                      {discountValidation.valid ? (
                        <span className="flex items-center gap-1">
                          <Check className="w-4 h-4" /> {discountValidation.display} applied!
                        </span>
                      ) : (
                        <span className="flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" /> {discountValidation.error}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6">
                  <div className="flex items-center gap-2 text-emerald-800 font-medium text-sm">
                    <Check className="w-4 h-4" />
                    You won&apos;t be charged today. Your trial starts now.
                  </div>
                </div>

                {isFreeAccess ? (
                  <button
                    onClick={handleActivateFree}
                    disabled={isActivatingFree}
                    data-testid="activate-free-btn"
                    className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-semibold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2"
                  >
                    <Check className="w-5 h-5" /> {isActivatingFree ? 'Activating...' : 'Activate free access — no card needed'}
                  </button>
                ) : (
                  <Elements stripe={stripePromise}>
                    <CardForm
                      user={user}
                      selectedTier={selectedTier}
                      discountCode={discountValidation?.valid ? discountCode : null}
                      onSuccess={handleSuccess}
                    />
                  </Elements>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default MembershipPage;
